import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { get } from '../../services/http'

import { getStartOfDay } from '../../utility/date';
import { getHeaders } from '../../utility/http';

export interface plannerState {
  newEvents: {
    events: any[]
    totalEvents: number
    nextSkip: number
    isLoading: boolean
    error?: string | undefined
  },
  acceptedEvents: {
    events: any[]
    totalEvents: number
    nextSkip: number
    isLoading: boolean
    error?: string | undefined
  },
  declinedEvents: {
    events: any[]
    totalEvents: number
    nextSkip: number
    isLoading: boolean
    error?: string | undefined
  },
  hasDeclinedEvents: boolean
}

const intialEvents = {
    events: [],
    totalEvents: 0,
    nextSkip: 0,
    isLoading: false
}

const initialState: plannerState = {
  newEvents: intialEvents,
  acceptedEvents: intialEvents,
  declinedEvents: intialEvents,
  hasDeclinedEvents: false
}

let lastRequest = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).toLocaleString('en-gb');

export const plannerSlice = createSlice({
  name: 'planner',
  initialState,
  reducers: {
    clearEvents: () => initialState
  },
  extraReducers(builder) {
    builder
      .addCase(fetchNewEvents.pending, (state, action) => {
        state.newEvents.isLoading = true
        state.newEvents.error = undefined
      })
      .addCase(fetchNewEvents.fulfilled, (state, action) => {
        if(action.payload) {
          state.newEvents = {
            events: action.payload.firstLoad ? action.payload.items : [...state.newEvents.events, ...action.payload.items],
            totalEvents: action.payload.totalItemCount,
            nextSkip: action.payload.nextSkip,
            isLoading: false
          }
        }
        
      })
      .addCase(fetchNewEvents.rejected, (state, action) => {
        state.newEvents.isLoading = false;
        state.newEvents.error = action.error.message
      })
      .addCase(fetchAllEvents.pending, (state, action) => {
        state.newEvents.isLoading = true
        state.acceptedEvents.isLoading = true
        state.declinedEvents.isLoading = true
        state.newEvents.error = undefined
      })
      .addCase(fetchAllEvents.fulfilled, (state, action) => {
        if(action.payload) {
          if(action.payload[0]){
            state.newEvents = {
              events: action.payload[0].items,
              totalEvents: action.payload[0].totalItemCount,
              nextSkip: action.payload[0].nextSkip,
              isLoading: false
            }
          }

          if(action.payload[1]) {
            state.acceptedEvents = {
              events: action.payload[1].items,
              totalEvents: action.payload[1].totalItemCount,
              nextSkip: action.payload[1].nextSkip,
              isLoading: false
            }
          }
          
          if(action.payload[2]) {
            state.declinedEvents = {
              events: action.payload[2].items,
              totalEvents: action.payload[2].totalItemCount,
              nextSkip: action.payload[2].nextSkip,
              isLoading: false
            }
          }
        }
      })
      .addCase(fetchAllEvents.rejected, (state, action) => {
        state.newEvents.isLoading = false
        state.newEvents.error = action.error.message
      })
      .addCase(fetchAcceptedEvents.pending, (state, action) => {
        state.acceptedEvents.isLoading = true
        state.acceptedEvents.error = undefined
      })
      .addCase(fetchAcceptedEvents.fulfilled, (state, action) => {
        if(action.payload) {
          state.acceptedEvents = {
            events: action.payload.firstLoad ? action.payload.items : [...state.acceptedEvents.events, ...action.payload.items],
            totalEvents: action.payload.totalItemCount,
            nextSkip: action.payload.nextSkip,
            isLoading: false
          }
        }
        
      })
      .addCase(fetchAcceptedEvents.rejected, (state, action) => {
        state.acceptedEvents.isLoading = true
        state.acceptedEvents.error = action.error.message
      })
      .addCase(fetchRejectedEvents.pending, (state, action) => {
        state.declinedEvents.isLoading = true
        state.declinedEvents.error = undefined
      })
      .addCase(fetchRejectedEvents.fulfilled, (state, action) => {
        if(action.payload) {
          state.declinedEvents = {
            events: action.payload.firstLoad ? action.payload.items : [...state.declinedEvents.events, ...action.payload.items],
            totalEvents: action.payload.totalItemCount,
            nextSkip: action.payload.nextSkip,
            isLoading: false
          }
        }
        
      })
      .addCase(fetchRejectedEvents.rejected, (state, action) => {
        state.declinedEvents.isLoading = true
        state.declinedEvents.error = action.error.message
      })
      
      .addCase(fetchEventCount.fulfilled, (state, action) => {
        if(action.payload) {
          state.hasDeclinedEvents = action.payload.totalItemCount > 0;
        } 
      })
      
  }
})

function updateLastRequest(){
  return new Date().toLocaleString('en-gb');
}

export const fetchNewEvents = createAsyncThunk('planner/fetchNewEvents', async (nextSkip: number) => {
  const response: any =  await get(`api/planner/events/upcoming/new?top=20&skip=${nextSkip}&since=${getStartOfDay()}`, getHeaders(lastRequest))
  lastRequest = updateLastRequest();
  return {...response.data, firstLoad: nextSkip === 0}
});

export const fetchAllEvents = createAsyncThunk('planner/fetchAllEvents', async (eventCounts: {new: number, accepted: number, declined: number}) => {
  const newEvents = get(`api/planner/events/upcoming/new?top=${eventCounts.new + 1}&skip=0&since=${getStartOfDay()}`, getHeaders(lastRequest));
  const acceptedEvents = get(`api/planner/events/upcoming/accepted?top=${eventCounts.accepted + 1}&skip=0&since=${getStartOfDay()}`, getHeaders(lastRequest)); 
  const declined = get(`api/planner/events/upcoming/declined?top=${eventCounts.declined + 1}&skip=0&since=${getStartOfDay()}`, getHeaders(lastRequest)); 
  
  const response = await Promise.all([newEvents, acceptedEvents, declined])
    .catch((response) => {
      throw new Error(response.message);
    })
  lastRequest = updateLastRequest();
  return response.map(res => res.data)
});

export const fetchAcceptedEvents = createAsyncThunk('planner/fetchAcceptedEvents', async (nextSkip: number) => {
  const response: any =  await get(`api/planner/events/upcoming/accepted?top=20&skip=${nextSkip}&since=${getStartOfDay()}`, getHeaders(lastRequest))
    .catch((response) => {
      throw new Error(response.message);
    });
  lastRequest = updateLastRequest();
  return {...response.data, firstLoad: nextSkip === 0}
});

export const fetchRejectedEvents = createAsyncThunk('planner/fetchRejectedEvents', async (nextSkip: number) => {
  const response: any =  await get(`api/planner/events/upcoming/declined?top=20&skip=${nextSkip}&since=${getStartOfDay()}`, getHeaders(lastRequest))
  .catch((response) => {
    throw new Error(response.message);
  });

  lastRequest = updateLastRequest();
  return {...response.data, firstLoad: nextSkip === 0}
});

export const fetchEventCount = createAsyncThunk('planner/fetchEventCount', async () => {
  const response: any =  await get(`api/planner/events/upcoming/declined?top=1&skip=0&since=${getStartOfDay()}`, getHeaders(lastRequest))
    .catch((response) => {
      throw new Error(response.message);
    });
  lastRequest = updateLastRequest();
 
  return response.data
});
// Action creators are generated for each case reducer function
export const { clearEvents } =  plannerSlice.actions


export default plannerSlice.reducer